/**
 * useValUsage — hook para consumo do Val AI (Phase 11.12).
 *
 * Lê o consumo do mês corrente + histórico de meses fechados via API
 * `/api/val/usage/{workspace_id}` (Supabase v360.val_usage).
 *
 * Escopo (workspace_id):
 *   • Se há uma sessão de impersonação ativa → usa o `workspace_id` dela.
 *   • Se há uma empresa cliente ativa no plano Consultoria → prefixa com
 *     `consult-` para separar o pool.
 *   • Default: "prototype".
 *
 * Retorno:
 *   { corrente: {consultas, tokens_in, tokens_out, custo_estimado_brl, frozen},
 *     historico: [{...mesmo formato, mes: "YYYY-MM"}],
 *     cota,        // total de consultas permitidas no mês (via useS360Tier)
 *     pct,         // % consumido (0-100)
 *     restante,    // consultas restantes
 *     loading,
 *     reload() }
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useS360Tier } from "./useS360Tier";
import { useS360Consult } from "@/context/S360ConsultContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function resolveWorkspaceId(hasConsultoria, empresaAtivaConsult) {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem("imp_session") : null;
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.workspace_id) return s.workspace_id;
    }
  } catch { /* noop */ }
  if (hasConsultoria && empresaAtivaConsult?.id) {
    return `consult-${empresaAtivaConsult.id}`;
  }
  return "prototype";
}

export function useValUsage() {
  const { limits } = useS360Tier();
  const { hasConsultoria, empresaAtiva } = useS360Consult();
  const workspaceId = useMemo(
    () => resolveWorkspaceId(hasConsultoria, empresaAtiva),
    [hasConsultoria, empresaAtiva]
  );

  const [data, setData] = useState({ corrente: null, historico: [] });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((x) => x + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!API_BASE) { setLoading(false); return; }
      setLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/val/usage/${encodeURIComponent(workspaceId)}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData({ corrente: j.corrente, historico: j.historico || [] });
      } catch {
        if (alive) setData({ corrente: null, historico: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [workspaceId, tick]);

  // Auto-refresh ao voltar aba (para pegar novos consumos disparados em outras views)
  useEffect(() => {
    const onFocus = () => reload();
    const onValAsk = () => reload();
    window.addEventListener("focus", onFocus);
    window.addEventListener("val:asked", onValAsk);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("val:asked", onValAsk);
    };
  }, [reload]);

  const cota = limits?.creditos_val_max || 0;
  const consultas = data.corrente?.consultas || 0;
  const pct = cota > 0 ? Math.min(100, Math.round((consultas / cota) * 100)) : 0;
  const restante = Math.max(0, cota - consultas);

  return {
    workspaceId,
    corrente: data.corrente || { consultas: 0, tokens_in: 0, tokens_out: 0, custo_estimado_brl: 0, frozen: false, mes: "" },
    historico: data.historico,
    cota, pct, restante,
    loading, reload,
  };
}
